import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="referral-place-search"
//
// Powers the "Add an organization" panel in Step 1 of the referral wizard.
// Searches Google Places for *establishments* (businesses/orgs, not raw
// addresses) via the modern Places API, then on selection POSTs the picked
// place — name, structured address, coordinates, place_id, website, phone — to
// `create_from_place`, which match-or-creates an HSDS org and returns selectable
// org cards. Adapted from claim_address_search_controller.js (the production
// reference for the custom-dropdown Places pattern).
const DEBOUNCE_MS = 200;

interface PlacePayload {
  name: string;
  website: string;
  phone: string;
  place_id: string;
  latitude: string;
  longitude: string;
  address_1: string;
  address_2: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
}

export default class extends Controller {
  static targets = ["input", "dropdown", "listbox", "results"];

  static values = {
    createUrl: String,
    country: { type: String, default: "us" },
  };

  declare inputTarget: HTMLInputElement;
  declare dropdownTarget: HTMLElement;
  declare listboxTarget: HTMLElement;
  declare resultsTarget: HTMLElement;

  declare createUrlValue: string;
  declare countryValue: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  declare predictions: any[];
  declare activeIndex: number;
  declare fetchSeq: number;
  declare lastQuery: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  declare sessionToken: any;
  declare debounceHandle: ReturnType<typeof setTimeout> | null;
  declare lastPayload: PlacePayload | null;
  declare boundInit?: () => void;
  declare onDocumentPointerDown: (event: Event) => void;

  connect() {
    this.predictions = [];
    this.activeIndex = -1;
    this.fetchSeq = 0;
    this.lastQuery = "";
    this.sessionToken = null;
    this.debounceHandle = null;
    this.lastPayload = null;

    this.onDocumentPointerDown = (event: Event) => this.handleOutsideClick(event);
    document.addEventListener("pointerdown", this.onDocumentPointerDown);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).google?.maps?.places) {
      this.mintSessionToken();
    } else {
      this.boundInit = () => this.mintSessionToken();
      window.addEventListener("google-maps-loaded", this.boundInit);
    }
  }

  disconnect() {
    if (this.boundInit) window.removeEventListener("google-maps-loaded", this.boundInit);
    document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    if (this.debounceHandle) clearTimeout(this.debounceHandle);
  }

  // One session token spans typing + the selection's fetchFields; rotate after
  // each completed pick so Google bills each "search → pick" as one session.
  mintSessionToken() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Token = (window as any).google?.maps?.places?.AutocompleteSessionToken;
    this.sessionToken = Token ? new Token() : null;
  }

  // === Input lifecycle ===========================================

  onInput() {
    const query = this.inputTarget.value.trim();
    if (this.debounceHandle) clearTimeout(this.debounceHandle);

    if (query === "") {
      this.predictions = [];
      this.activeIndex = -1;
      this.renderList();
      this.closeList();
      return;
    }
    if (query === this.lastQuery) return;

    this.debounceHandle = setTimeout(() => this.fetchPredictions(query), DEBOUNCE_MS);
  }

  onFocus() {
    if (this.predictions.length > 0) this.openList();
  }

  onKeyDown(event: KeyboardEvent) {
    const key = event.key;

    if (key === "Enter") {
      event.preventDefault();
      const pick = this.predictions[this.activeIndex];
      if (pick) this.selectPrediction(pick);
      return;
    }
    if (key === "Escape") {
      event.preventDefault();
      this.closeList();
      return;
    }
    if (this.predictions.length === 0) return;

    if (key === "ArrowDown") {
      event.preventDefault();
      this.setActive((this.activeIndex + 1) % this.predictions.length);
    } else if (key === "ArrowUp") {
      event.preventDefault();
      const next = this.activeIndex <= 0 ? this.predictions.length - 1 : this.activeIndex - 1;
      this.setActive(next);
    }
  }

  // === Google Places fetch =======================================

  async fetchPredictions(query: string) {
    this.lastQuery = query;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Suggest = (window as any).google?.maps?.places?.AutocompleteSuggestion;
    if (!Suggest?.fetchAutocompleteSuggestions) return;

    const seq = ++this.fetchSeq;

    try {
      const { suggestions } = await Suggest.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: this.sessionToken,
        includedPrimaryTypes: ["establishment"],
        includedRegionCodes: [this.countryValue],
      });

      if (seq !== this.fetchSeq) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.predictions = suggestions.map((s: any) => s.placePrediction).filter(Boolean);
      this.activeIndex = -1;
      this.renderList();
      this.openList();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Places autocomplete error:", err);
      this.predictions = [];
      this.renderList();
      this.closeList();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async selectPrediction(prediction: any) {
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: [
          "id",
          "displayName",
          "formattedAddress",
          "location",
          "addressComponents",
          "websiteURI",
          "nationalPhoneNumber",
        ],
      });

      const loc = place.location;
      const lat = typeof loc?.lat === "function" ? loc.lat() : loc?.lat;
      const lng = typeof loc?.lng === "function" ? loc.lng() : loc?.lng;

      const name = place.displayName || prediction.mainText?.text || prediction.text?.text || "";
      this.inputTarget.value = name;
      this.closeList();
      this.inputTarget.blur();
      this.lastQuery = name;
      this.mintSessionToken();

      const payload = this.buildPayload(place, name, lat, lng);
      this.lastPayload = payload;
      this.submitPlace(payload, false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Place fetch error:", err);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private buildPayload(place: any, name: string, lat: unknown, lng: unknown): PlacePayload {
    const parts: Record<string, { long: string; short: string }> = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (place.addressComponents || []).forEach((component: any) => {
      const type = component.types?.[0];
      if (type) parts[type] = { long: component.longText || "", short: component.shortText || "" };
    });
    const get = (type: string, key: "long" | "short" = "long") => (parts[type] ? parts[type][key] : "");

    return {
      name,
      website: place.websiteURI || "",
      phone: place.nationalPhoneNumber || "",
      place_id: place.id || "",
      latitude: lat == null ? "" : String(lat),
      longitude: lng == null ? "" : String(lng),
      address_1: `${get("street_number")} ${get("route")}`.trim(),
      address_2: get("subpremise"),
      city: get("locality") || get("sublocality") || get("administrative_area_level_2"),
      state_province: get("administrative_area_level_1", "short"),
      postal_code: get("postal_code"),
      country: get("country", "short") || "US",
    };
  }

  // Re-POST the last pick forcing creation, past the dedupe candidates.
  addAsNew() {
    if (this.lastPayload) this.submitPlace(this.lastPayload, true);
  }

  private async submitPlace(payload: PlacePayload, forceCreate: boolean) {
    const body = new URLSearchParams();
    Object.entries(payload).forEach(([k, v]) => body.set(k, v));
    if (forceCreate) body.set("force_create", "1");

    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          Accept: "text/html",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": token,
        },
        credentials: "same-origin",
        body: body.toString(),
      });
      if (!response.ok) return;
      this.resultsTarget.innerHTML = await response.text();
      this.autoSelectSingleResult();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("create_from_place error:", err);
    }
  }

  // A single returned card (a fresh create or an exact match) is auto-selected
  // so the user doesn't have to tap it. Dedupe candidate lists carry the "add as
  // new" affordance and need a manual pick, so they're left alone. Deferred a
  // frame so Stimulus binds the freshly-injected card's `selectOrg` action first.
  private autoSelectSingleResult() {
    if (this.resultsTarget.querySelector('[data-action*="addAsNew"]')) return;
    const cards = this.resultsTarget.querySelectorAll<HTMLElement>(
      '[data-referral-wizard-target="orgCard"]',
    );
    if (cards.length !== 1) return;
    const card = cards[0];
    requestAnimationFrame(() => card.click());
  }

  // === Dropdown rendering / ARIA =================================

  renderList() {
    if (this.predictions.length === 0) {
      this.listboxTarget.innerHTML = "";
      return;
    }
    this.listboxTarget.innerHTML = this.predictions.map((p, i) => this.rowMarkup(p, i)).join("");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rowMarkup(prediction: any, i: number): string {
    const isActive = i === this.activeIndex;
    const primary = this.escapeHtml(prediction.mainText?.text || prediction.text?.text || "");
    const secondary = prediction.secondaryText?.text ? this.escapeHtml(prediction.secondaryText.text) : "";
    const baseRow = "flex items-start gap-2.5 px-2 py-1.5 cursor-pointer rounded-lg transition-colors";
    const stateRow = isActive ? "bg-primary/10" : "hover:bg-base-200";

    return `
      <li id="referral-place-option-${i}"
          role="option"
          aria-selected="${isActive}"
          data-index="${i}"
          data-action="mousedown->referral-place-search#onRowMouseDown click->referral-place-search#onRowClick mousemove->referral-place-search#onRowHover"
          class="${baseRow} ${stateRow}">
        <span class="shrink-0 mt-0.5 text-base-content/40">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
            <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4"/>
          </svg>
        </span>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-base-content leading-snug truncate">${primary}</p>
          ${secondary ? `<p class="text-xs text-base-content/60 leading-snug truncate mt-0.5">${secondary}</p>` : ""}
        </div>
      </li>
    `;
  }

  private escapeHtml(str: string): string {
    return String(str).replace(
      /[&<>"']/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string,
    );
  }

  private setActive(index: number) {
    this.activeIndex = index;
    this.renderList();
    const id = `referral-place-option-${index}`;
    this.inputTarget.setAttribute("aria-activedescendant", id);
    document.getElementById(id)?.scrollIntoView({ block: "nearest" });
  }

  private openList() {
    if (this.predictions.length === 0) {
      this.closeList();
      return;
    }
    this.dropdownTarget.classList.remove("hidden");
    this.inputTarget.setAttribute("aria-expanded", "true");
  }

  private closeList() {
    this.dropdownTarget.classList.add("hidden");
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.removeAttribute("aria-activedescendant");
  }

  // === Row events ================================================

  onRowMouseDown(event: Event) {
    event.preventDefault();
  }

  onRowClick(event: Event) {
    const i = parseInt((event.currentTarget as HTMLElement).dataset.index || "", 10);
    const pick = this.predictions[i];
    if (pick) this.selectPrediction(pick);
  }

  onRowHover(event: Event) {
    const i = parseInt((event.currentTarget as HTMLElement).dataset.index || "", 10);
    if (Number.isFinite(i) && i !== this.activeIndex) this.setActive(i);
  }

  private handleOutsideClick(event: Event) {
    if (this.element.contains(event.target as Node)) return;
    this.closeList();
  }
}
